import type { LabelField, LabelSettings } from "../types";
import { ZPL_FONT_WIDTHS } from "./textUtils";
import { analyzeTextWrapping } from "./zplGenerator";

/**
 * Get text lines for canvas preview that matches ZPL output
 */
export function getTextLinesForPreview(
  field: LabelField,
  settings: LabelSettings,
): string[] {
  if (field.type !== "text") return [];

  // Handle truncation
  if (field.truncate) {
    const truncatedText = truncateTextForPreview(field);
    return [truncatedText];
  }

  const wrappingResult = analyzeTextWrapping(
    field.content,
    field.fontSize || 20,
    field.fontFamily || "0",
    field.width,
    false,
    settings.dpi,
  );

  return wrappingResult.lines;
}

/**
 * Truncate text for preview (matches ZPL truncation logic)
 */
function truncateTextForPreview(field: LabelField): string {
  const { content, fontSize = 20, fontFamily = "0", width } = field;

  if (!content) return "";

  const baseCharWidth = ZPL_FONT_WIDTHS[fontFamily] || ZPL_FONT_WIDTHS["0"];
  const charWidthInDots = baseCharWidth * (fontSize / 20); // Scale by fontSize (20 is base size)
  const maxChars = Math.floor(width / charWidthInDots);

  if (content.length <= maxChars) {
    return content;
  }

  // Truncate and add ellipsis if there's room
  if (maxChars > 3) {
    return `${content.substring(0, maxChars - 3)}...`;
  }

  if (maxChars > 0) {
    return content.substring(0, maxChars);
  }

  return "";
}

/**
 * Calculate display dimensions for text field in canvas
 */
export function calculateTextDisplayDimensions(
  field: LabelField,
  settings: LabelSettings,
): { width: number; height: number } {
  if (field.type !== "text") {
    return { width: field.width, height: field.height };
  }

  const wrappingResult = analyzeTextWrapping(
    field.content,
    field.fontSize || 20,
    field.fontFamily || "0",
    field.width,
    false,
    settings.dpi,
  );

  return {
    width: field.width, // Always show full container width
    height: wrappingResult.totalHeight,
  };
}
